"use client";
import React, { useContext } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { timezoneconstant } from "@/constants/timezone";
import { usercontext } from "@/lib/context";
import { logger } from "@/lib/logger";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const DEFAULT_TIMEZONE = "Asia/Kolkata";

const TIME_FORMATS = [
  "YYYY-MM-DD HH:mm",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD hh:mm A",
  "YYYY-MM-DD hh:mm:ss A",
  "YYYY-MM-DD",
];

const getValidTimezone = (tz) => {
  if (!tz || typeof tz !== "string") return DEFAULT_TIMEZONE;
  const trimmed = tz.trim();
  if (
    trimmed.includes(" ") ||
    trimmed.includes("YYYY") ||
    trimmed.length > 50
  ) {
    return DEFAULT_TIMEZONE;
  }
  try {
    Intl.DateTimeFormat(undefined, { timeZone: trimmed });
    return trimmed;
  } catch (e) {
    return DEFAULT_TIMEZONE;
  }
};

const areTimezonesEqual = (tz1, tz2) => {
  if (!tz1 || !tz2) return false;
  const norm1 = tz1
    .toLowerCase()
    .trim()
    .replace("asia/calcutta", "asia/kolkata");
  const norm2 = tz2
    .toLowerCase()
    .trim()
    .replace("asia/calcutta", "asia/kolkata");
  return norm1 === norm2;
};

const ConvertedTime = ({ date, time, timezone: rawEventTz }) => {
  const context = useContext(usercontext);
  const rawUserTz = context?.timeZone;

  const eventTz = getValidTimezone(rawEventTz);
  const userTz = rawUserTz ? getValidTimezone(rawUserTz) : eventTz;

  if (!date || !time) return null;
  const eventDateTimeStr = `${date} ${time}`.trim();

  let parsed = dayjs(eventDateTimeStr, TIME_FORMATS, true);
  if (!parsed.isValid()) {
    parsed = dayjs(eventDateTimeStr, TIME_FORMATS);
  }
  if (!parsed.isValid()) {
    parsed = dayjs(eventDateTimeStr);
  }
  if (!parsed.isValid()) {
    parsed = dayjs(date);
  }
  if (!parsed.isValid()) {
    return null;
  }

  const formattedStr = parsed.format("YYYY-MM-DD HH:mm:ss");
  let eventDate;
  try {
    eventDate = dayjs.tz(formattedStr, "YYYY-MM-DD HH:mm:ss", eventTz);
  } catch (e) {
    eventDate = dayjs(formattedStr);
  }

  if (!eventDate || !eventDate.isValid()) {
    return null;
  }

  let displayTime;
  try {
    displayTime = eventDate.tz(userTz);
  } catch (e) {
    displayTime = eventDate;
  }

  if (!displayTime || !displayTime.isValid()) {
    logger.error("Invalid timezone conversion", {
      eventTz,
      userTz,
    });
    return null;
  }

  const isSameTimezone = areTimezonesEqual(eventTz, userTz);

  return (
    <>
      {displayTime.format("ddd, DD MMM YYYY • hh:mm A")}
      {!isSameTimezone && <> · {timezoneconstant[eventTz] || eventTz}</>}
    </>
  );
};

export default ConvertedTime;
